import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Clock, ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import { CONTACT_EMAIL } from "@/lib/pricingData";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { phase, data, error } = usePaymentStatus(sessionId);

  return (
    <main data-testid="checkout-success-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
      <div className="bg-white border border-brand-line rounded-md p-8 md:p-12 text-center">
        {phase === "polling" && <PhasePolling />}
        {phase === "paid" && data && <PhasePaid data={data} />}
        {phase === "expired" && <PhaseExpired />}
        {phase === "unpaid" && <PhaseUnpaid />}
        {phase === "error" && <PhaseError error={error} />}
      </div>
    </main>
  );
}

function BackToPricing({ solid, children = "Back to pricing" }) {
  return (
    <Link
      to="/pricing"
      className={
        solid
          ? "mt-8 inline-flex items-center gap-2 rounded-md bg-brand-forest hover:bg-brand-forestHover text-brand-bone px-6 py-3 text-sm font-semibold"
          : "mt-8 inline-flex items-center gap-2 rounded-md border border-brand-line text-brand-forest px-6 py-3 text-sm font-semibold"
      }
    >
      {children}
    </Link>
  );
}

function PhasePolling() {
  return (
    <>
      <Clock size={56} weight="duotone" className="text-brand-copper mx-auto animate-pulse" />
      <h1 className="mt-6 font-serif text-3xl md:text-4xl text-brand-forest">
        Confirming your payment…
      </h1>
      <p className="mt-4 text-brand-ink/70">
        We're checking with Stripe. This usually takes 5–15 seconds.
      </p>
    </>
  );
}

function PhasePaid({ data }) {
  return (
    <>
      <CheckCircle size={64} weight="fill" className="text-brand-forest mx-auto" />
      <h1 className="mt-6 font-serif text-3xl md:text-4xl text-brand-forest">
        Welcome aboard.
      </h1>
      <p className="mt-4 text-brand-ink/75 max-w-xl mx-auto">
        Your <strong className="text-brand-forest">{data.tier}</strong> payment of{" "}
        <strong className="text-brand-copper">
          {Number(data.amount_total).toLocaleString("en-CA", { style: "currency", currency: "CAD" })}{" "}
          {data.currency.toUpperCase()}
        </strong>{" "}
        has been received. We'll follow up within one business day with onboarding details and your advisor login.
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/calculator"
          data-testid="success-cta-calculator"
          className="inline-flex items-center gap-2 rounded-md bg-brand-forest hover:bg-brand-forestHover text-brand-bone px-6 py-3 text-sm font-semibold transition-colors"
        >
          Open the calculator <ArrowRight size={14} weight="bold" />
        </Link>
        <a
          href={`mailto:${CONTACT_EMAIL}?subject=I%20just%20signed%20up%20for%20Build%20A%20Bank`}
          className="inline-flex items-center gap-2 rounded-md border border-brand-line text-brand-forest hover:bg-brand-cream px-6 py-3 text-sm font-semibold transition-colors"
        >
          <EnvelopeSimple size={14} weight="duotone" /> Reply to start onboarding
        </a>
      </div>
    </>
  );
}

function PhaseExpired() {
  return (
    <>
      <XCircle size={56} weight="duotone" className="text-brand-muted mx-auto" />
      <h1 className="mt-6 font-serif text-3xl md:text-4xl text-brand-forest">
        That checkout expired.
      </h1>
      <p className="mt-4 text-brand-ink/70">
        No charge was made. Pick a tier again whenever you're ready.
      </p>
      <BackToPricing solid>
        Back to pricing <ArrowRight size={14} weight="bold" />
      </BackToPricing>
    </>
  );
}

function PhaseUnpaid() {
  return (
    <>
      <Clock size={56} weight="duotone" className="text-brand-copper mx-auto" />
      <h1 className="mt-6 font-serif text-3xl md:text-4xl text-brand-forest">
        Still processing…
      </h1>
      <p className="mt-4 text-brand-ink/70 max-w-xl mx-auto">
        Stripe is taking a little longer than usual to confirm. If you completed the payment, you'll get a confirmation email within a few minutes. You can safely close this page.
      </p>
      <BackToPricing />
    </>
  );
}

function PhaseError({ error }) {
  return (
    <>
      <XCircle size={56} weight="duotone" className="text-red-700 mx-auto" />
      <h1 className="mt-6 font-serif text-3xl md:text-4xl text-brand-forest">
        We couldn't confirm this session.
      </h1>
      <p className="mt-4 text-brand-ink/70">{error || "Please try again or contact support."}</p>
      <BackToPricing />
    </>
  );
}
